import { useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { ChapterMarker } from './shared';
import { CONTACT } from '../assets';

/* ── Magnetic Link ────────────────────────────────────────── */
function MagneticLink({ children, href, className, ...props }) {
  const ref = useRef(null);
  const textRef = useRef(null);

  const handleMove = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    if (textRef.current) {
      textRef.current.style.transform = `translate(${x * 0.3}px, ${y * 0.3}px)`;
    }
  }, []);

  const handleLeave = useCallback(() => {
    if (textRef.current) {
      textRef.current.style.transform = 'translate(0px, 0px)';
    }
  }, []);

  return (
    <a
      ref={ref}
      href={href}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      className={className}
      {...props}
    >
      <div ref={textRef} className="flex items-baseline justify-between w-full" style={{ transition: 'transform 0.3s cubic-bezier(0.23, 1, 0.32, 1)' }}>
        {children}
      </div>
    </a>
  );
}

export default function Ch10Partner() {
  return (
    <section id="ch-10" data-testid="chapter-10" className="relative min-h-screen bg-[#0A0A0A] px-6 md:px-16 pt-28 md:pt-40 pb-12 flex flex-col">
      <ChapterMarker num="12" title="Partner With Us" />

      <div className="flex-1 flex flex-col justify-center max-w-5xl">
        <motion.span
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="font-accent text-[10px] text-[#8C5A35] mb-8 block"
        >
          The final chapter is yours
        </motion.span>
        <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl xl:text-8xl leading-[0.95] tracking-tight">
          <span className="block overflow-hidden">
            <motion.span initial={{ y: '110%' }} whileInView={{ y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9, ease: [0.25, 1, 0.5, 1] }} className="block">
              Let's craft
            </motion.span>
          </span>
          <span className="block overflow-hidden">
            <motion.span initial={{ y: '110%' }} whileInView={{ y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9, delay: 0.12, ease: [0.25, 1, 0.5, 1] }} className="block">
              <span className="italic text-[#B89B72]">together.</span>
            </motion.span>
          </span>
        </h2>

        <div className="mt-16 md:mt-20 space-y-1">
          <MagneticLink
            href={CONTACT.whatsappEnquiry}
            target="_blank"
            rel="noreferrer"
            data-testid="partner-whatsapp-link"
            className="group flex border-b border-[#2A2A2A] py-6 md:py-8 hover:border-[#B89B72] transition-colors duration-300"
          >
            <div>
              <span className="font-accent text-[9px] text-[#525252] block mb-2">WhatsApp</span>
              <span className="font-display text-2xl md:text-4xl font-light group-hover:text-[#B89B72] transition-colors duration-300">{CONTACT.phoneDisplay}</span>
            </div>
            <ArrowUpRight className="w-6 h-6 text-[#525252] group-hover:text-[#B89B72] transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 ml-auto self-center" strokeWidth={1.2} />
          </MagneticLink>
          <MagneticLink
            href={CONTACT.email}
            data-testid="partner-email-link"
            className="group flex border-b border-[#2A2A2A] py-6 md:py-8 hover:border-[#B89B72] transition-colors duration-300"
          >
            <div>
              <span className="font-accent text-[9px] text-[#525252] block mb-2">Email</span>
              <span className="font-display text-2xl md:text-4xl font-light group-hover:text-[#B89B72] transition-colors duration-300">{CONTACT.emailDisplay}</span>
            </div>
            <ArrowUpRight className="w-6 h-6 text-[#525252] group-hover:text-[#B89B72] transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 ml-auto self-center" strokeWidth={1.2} />
          </MagneticLink>
        </div>
      </div>

      <footer className="mt-20 md:mt-28 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <div className="font-display text-xl text-[#F5F5F0]">Pakkar Tanveer <span className="italic text-[#B89B72]">Exports</span></div>
          <div className="font-body text-xs text-[#525252] mt-2">Premium leather jackets — Japan · USA · Europe · South Korea</div>
        </div>
        <div className="font-accent text-[9px] text-[#525252]">© {new Date().getFullYear()} Pakkar Tanveer Exports. 35+ years of craftsmanship.</div>
      </footer>
    </section>
  );
}
